import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EMPTY, Subject, takeUntil, finalize, catchError } from 'rxjs';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Divider } from 'primeng/divider';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import {
  CadObject,
  CadObjectSummary,
} from '../../../../models/cad-object.model';
import { DFMCheck } from '../../../../models/dfm-check.model';
import { QuoteRevisionItem } from '../../../../models/quote-revision-item.model';
import { QuoteItemData } from '../../../../models/quote-item-data.model';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';
import { CadFileService } from '../../../services/cad-file.service';
import { CadViewerComponent } from '../../../cad-viewer/cad-viewer.component';
import { QuoteRevisionItemCapexInformationComponent } from './capex-information.component';
import { QuoteRevisionItemConsumablesInformationComponent } from './consumables-information.component';
import { QuoteRevisionItemDfmChecksComponent } from './dfm-checks.component';
import { QuoteRevisionItemDrawingInformationComponent } from './drawing-information.component';
import { QuoteRevisionItemDetailsPanelComponent } from './item-details.component';
import { CadObjectTreeComponent } from '../../../shared/cad-object-tree/cad-object-tree.component';
import { QuoteRevisionItemPricingInformationComponent } from './pricing-information.component';
import { QuoteItemLinkCadComponent } from '../quote-revision-item-add/quote-item-data-editor/quote-item-link-cad/quote-item-link-cad.component';
import { CadPropertiesComponent } from '../../../shared/cad-properties/cad-properties.component';

type QuoteRevisionItemDetailsPanel =
  | 'details'
  | 'properties'
  | 'dfm'
  | 'pricing'
  | 'drawing'
  | 'capex'
  | 'consumables';

@Component({
  selector: 'app-quote-revision-item-details',
  imports: [
    Button,
    CadPropertiesComponent,
    CadViewerComponent,
    Dialog,
    Divider,
    QuoteRevisionItemCapexInformationComponent,
    QuoteRevisionItemConsumablesInformationComponent,
    QuoteRevisionItemDfmChecksComponent,
    QuoteRevisionItemDrawingInformationComponent,
    QuoteRevisionItemDetailsPanelComponent,
    CadObjectTreeComponent,
    QuoteRevisionItemPricingInformationComponent,
    QuoteItemLinkCadComponent,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
  ],
  templateUrl: './quote-revision-item-details.component.html',
})
export class QuoteRevisionItemDetailsComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) item!: QuoteRevisionItem;
  @Input({ required: true }) currencyCode!: string;
  @Input() visible = false;
  @Input() readOnly = false;
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Input() consumablesEnabled = false;
  @Input() activePanel: QuoteRevisionItemDetailsPanel = 'details';

  get showConsumablesTab(): boolean {
    return this.consumablesEnabled && (this.item.children?.length ?? 0) === 0;
  }

  get detailsTab(): QuoteRevisionItemDetailsPanel {
    if (!this.showConsumablesTab && this.activePanel === 'consumables') {
      return 'details';
    }
    return this.activePanel;
  }
  @Output() dialogClosed = new EventEmitter<void>();

  private readonly revisionItemService = inject(QuoteRevisionItemService);
  private readonly cadFiles = inject(CadFileService);
  private readonly destroy$ = new Subject<void>();
  // Item updates arrive as new objects after any edit, so only reload CAD when
  // the linked object IDs actually change.
  private loadedCadObjectIdsKey: string | null = null;
  loading = false;
  loadedCadObjects: CadObject[] = [];
  selectedCadObject: CadObject | null = null;
  hoveredCadObjectId: number | null = null;
  visibleCadObjectIds: Set<number> | null = null;
  selectedDFMCheck: DFMCheck | null = null;
  relinkCadVisible = false;
  relinkLinkItems: QuoteItemData[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.visible) return;
    const idsKey = this.cadObjects.map((cadObject) => cadObject.id).join(',');
    if (changes['visible'] || idsKey !== this.loadedCadObjectIdsKey) {
      this.loadedCadObjectIdsKey = idsKey;
      this.loadCadObjects();
    }
  }

  get cadObjects(): CadObjectSummary[] {
    return this.item.cad_objects.length
      ? this.item.cad_objects
      : this.item.inherited_cad_objects;
  }

  get contentMode(): 'cad' | 'drawing' | 'empty' {
    if (this.cadObjects.length) return 'cad';
    if (
      this.item.drawing_files.length ||
      this.item.inherited_drawing_files.length
    ) {
      return 'drawing';
    }
    return 'empty';
  }

  loadCadObjects(): void {
    this.loadedCadObjects = [];
    this.selectedCadObject = null;
    this.selectedDFMCheck = null;
    if (!this.cadObjects.length || this.loading) return;
    this.loading = true;
    this.cadFiles
      .loadCadObjects(this.cadObjects.map((cadObject) => cadObject.id))
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => EMPTY),
        finalize(() => (this.loading = false)),
      )
      .subscribe((objects) => {
        this.loadedCadObjects = objects;
        this.selectedCadObject = objects[0] ?? null;
      });
  }

  onRelinkCadRequested(): void {
    if (this.readOnly) return;
    this.relinkLinkItems = [
      {
        persistent_id: this.item.persistent_id,
        parent_id: null,
        sibling_order: this.item.sibling_order,
        name: this.item.name,
        cad_object_ids: this.item.cad_objects.map((cadObject) => cadObject.id),
        cad_object_names: this.item.cad_objects.map(
          (cadObject) => cadObject.name,
        ),
      },
    ];
    this.relinkCadVisible = true;
  }

  onRelinkCadConfirmed(items: QuoteItemData[]): void {
    if (this.readOnly) return;
    const linkedItem = items.find(
      (item) => item.persistent_id === this.item.persistent_id,
    );
    if (!linkedItem) {
      return;
    }
    this.relinkCadVisible = false;
    this.relinkLinkItems = [];
    void this.revisionItemService.updateItem(this.item.persistent_id, {
      cad_object_ids: linkedItem.cad_object_ids,
    });
  }

  onRelinkCadClosed(): void {
    this.relinkCadVisible = false;
    this.relinkLinkItems = [];
  }

  selectCadObject(cadObject: CadObject): void {
    this.selectedCadObject = cadObject;
    this.selectedDFMCheck = null;
  }

  onSelectedCadObjectChange(cadObject: CadObject | null): void {
    this.selectedCadObject = cadObject;
    if (
      this.selectedDFMCheck &&
      cadObject &&
      this.selectedDFMCheck.cad_object_id !== cadObject.id
    ) {
      this.selectedDFMCheck = null;
    }
  }

  onSelectedDFMCheckChange(check: DFMCheck | null): void {
    this.selectedDFMCheck = check;
    if (!check) return;
    this.selectedCadObject =
      this.loadedCadObjects.find((cad) => cad.id === check.cad_object_id) ??
      this.selectedCadObject;
  }

  onDialogShow(): void {
    window.dispatchEvent(new Event('resize'));
  }

  closeDialog(): void {
    this.visible = false;
    this.dialogClosed.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
